import { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Clock, Users, Tag, MessageSquareText, ChevronDown, Phone, MessageSquare, MessageCircle } from "lucide-react";
import { TagChips } from "./TagChips";
import { TagEditor } from "./TagEditor";

const TYPE_META = {
  large: { label: "Large", dot: "bg-brand", bar: "bg-brand", tint: "bg-brand/[0.07]", border: "border-brand/40", text: "text-brand-700" },
  medium: { label: "Medium", dot: "bg-amber-500", bar: "bg-amber-500", tint: "bg-amber-500/[0.08]", border: "border-amber-500/40", text: "text-amber-700" },
  small: { label: "Small", dot: "bg-sage", bar: "bg-sage", tint: "bg-sage/[0.08]", border: "border-sage/40", text: "text-sage-600" },
  umbrella: { label: "Umbrella", dot: "bg-sea", bar: "bg-sea", tint: "bg-sea/[0.08]", border: "border-sea/40", text: "text-sea-600" },
};

function fmtTime(dt) {
  if (!dt) return "";
  const t = dt.split(" ")[1] || "";
  return t.slice(0, 5);
}

const clientKey = (r) => r.client_id || `b-${r.booking_id}`;

export const GuestCard = ({ reservation, overlay = false, onSaveTags, tapToAssign = false, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = TYPE_META[reservation.lounge_type] || TYPE_META.small;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `res-${reservation.booking_id}`,
    data: { reservation },
  });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;
  const pax = reservation.party_size;
  const hasNotes = !!reservation.special_requests;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(overlay ? {} : listeners)}
      {...(overlay ? {} : attributes)}
      data-testid={`guest-card-${reservation.booking_id}`}
      onClick={() => { if (overlay) return; if (tapToAssign && onSelect) onSelect(reservation); else setExpanded((v) => !v); }}
      className={[
        "group relative select-none overflow-hidden rounded-xl border p-3 pl-4 transition-shadow duration-150",
        overlay ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing",
        meta.tint, meta.border,
        overlay ? "shadow-xl ring-2 ring-black/5 rotate-1" : "shadow-sm hover:shadow-md",
        isDragging && !overlay ? "opacity-30" : "opacity-100",
      ].join(" ")}
    >
      <span className={`absolute left-0 top-0 h-full w-1.5 ${meta.bar}`} />
      <div className="flex items-start gap-2">
        <span
          data-testid={`drag-handle-${reservation.booking_id}`}
          className="mt-0.5 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500"
          aria-hidden="true"
        >
          <GripVertical size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate font-display text-[16px] leading-tight text-slate-900">
              {reservation.guest_name}
            </p>
            <div className="flex shrink-0 items-center gap-1">
              {!overlay && reservation.phone && (
                <>
                  <a data-testid={`call-guest-${reservation.booking_id}`} href={`tel:${reservation.phone}`} onClick={(e) => e.stopPropagation()} title={`Call ${reservation.guest_name}`}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-brand-700"><Phone size={13} /></a>
                  <a data-testid={`text-guest-${reservation.booking_id}`} href={`sms:${reservation.phone}`} onClick={(e) => e.stopPropagation()} title="Text guest"
                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-brand-700"><MessageSquare size={13} /></a>
                  <a data-testid={`whatsapp-guest-${reservation.booking_id}`}
                    href={`https://wa.me/${reservation.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${reservation.guest_name}, this is TRIBE Beach Lounge in Tigaki about your sunbed reservation. `)}`}
                    target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="WhatsApp guest"
                    className="flex h-6 w-6 items-center justify-center rounded-md text-emerald-600 transition-colors hover:bg-emerald-50"><MessageCircle size={13} /></a>
                </>
              )}
              {!overlay && onSaveTags && (
                <TagEditor clientId={clientKey(reservation)} tags={reservation.tags} onSave={onSaveTags}>
                  <button
                    data-testid={`tag-btn-${reservation.booking_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-brand-700"
                    title="Edit tags"
                  >
                    <Tag size={13} />
                  </button>
                </TagEditor>
              )}
              {!overlay && (
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                />
              )}
            </div>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {fmtTime(reservation.start_date)}–{fmtTime(reservation.end_date)}
            </span>
            <span className={`font-semibold ${meta.text}`}>{meta.label}</span>
            {pax > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/[0.06] px-1.5 py-0.5 font-semibold text-slate-700"
                data-testid={`party-${reservation.booking_id}`}>
                <Users size={12} /> {pax} pax
              </span>
            )}
          </div>

          <TagChips tags={reservation.tags} className="mt-2" />

          {/* Expanded details — notes only (no phone / no other info) */}
          {expanded && !overlay && (
            <div className="mt-2 border-t border-black/5 pt-2" data-testid={`guest-details-${reservation.booking_id}`}>
              <div className="flex items-start gap-1.5 text-[13px] leading-snug text-slate-600">
                <MessageSquareText size={13} className="mt-0.5 shrink-0 text-slate-400" />
                {hasNotes ? (
                  <span className="italic">{reservation.special_requests}</span>
                ) : (
                  <span className="italic text-slate-400">No notes</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const shortName = (name = "") => {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0]} ${parts.slice(1).join(" ").slice(0, 5)}`;
};

export { TYPE_META, fmtTime, clientKey, shortName };
